import clsx from "clsx";
import { type FC, type ReactNode, useCallback, useEffect, useState } from "react";
import { useBoolean, useCopyToClipboard } from "usehooks-ts";

import { userHasGlobalAuthority } from "../../../../../shared/capabilities";
import {
  CourseRole,
  MaxRole,
  OrgRole,
  SectionRole,
  UserRole,
} from "../../../../../shared/types/roles";
import { AddUserModal } from "../AddUserModal";
import { BulkImportMenu } from "../bulk-import/BulkImportMenu";

import { AdminUserOptionsMenu } from "./AdminUserOptionsMenu";
import { ManageCourseAdminModal } from "./ManageCourseAdminModal";
import { ExtendedOrgRoleType, OrgRoleIconWithTooltip } from "./UserRoleIcons";

import { InviteStatus } from "api/api-data-types";
import { serverManageCourseAdmin } from "api/api-server-actions";
import { EditOrgUserModal } from "components/modals/EditOrgUserModal";
import type { BulkImportType, OrgUserType, UserType } from "components/server-types";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TR } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { showConfirmationModal } from "mds/components/Modal";
import {
  SortableBody,
  SortableHeader,
  SortableTable,
  SortableTableWrapper,
} from "mds/components/SortableTable";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { ArrowDownToLineIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { slices, storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  courseGroupingUserSelectors,
  courseUserSelectors,
  orgUserSelectors,
  sectionSelectors,
  selectCurrentOrg,
  selectEnabledSectionUsersByRole,
  selectIsEditModeEnabled,
  userSelectors,
} from "store/selectors";
import { selectUserInviteStatuses } from "store/selectors/computed";
import { sortByFirstName } from "store/selectors/sorting";
import { useListSelector } from "store/store-hooks";
import { toastErrorMessage, toastLocalizedOperationCatcher } from "utils/alerts";
import { downloadCSVFile } from "utils/file";
import { generateOrgUsersCSV } from "utils/reporting";
import { getSectionResourceRole } from "utils/section";
import { useFetchUserInviteStatuses } from "utils/user-invite-status";
import { createImpersonationUrl, getUserFullName } from "utils/user-utils";

type AdminUserEntry = {
  user: UserType;
  orgUser: OrgUserType;
  tooltipSectionRoleTable: ReactNode;
  inviteStatus: InviteStatus;
  maxRole: MaxRole;
  isCourseAdmin: boolean;
};

const ORG_ROLE_SORT_ORDER: Record<OrgRole | CourseRole, number> = {
  admin: 0,
  course_admin: 1,
  recording_agent: 3, // filtered out above; here for exhaustiveness
};

const getOrgRoleSortOrder = (role: OrgRole | CourseRole | null): number =>
  role == null ? 2 : ORG_ROLE_SORT_ORDER[role];

const MAX_ROLE_SORT_ORDER: Record<MaxRole, number> = {
  super_admin: 0,
  global_admin: 1,
  org_admin: 2,
  course_admin: 3,
  instructor: 4,
  section_assistant: 5,
  student: 6,
};

const getDisplayOrgRole = (
  user: UserType,
  orgUser: OrgUserType,
  isCourseAdmin = false,
): ExtendedOrgRoleType => {
  if (user.role) return user.role;
  if (isCourseAdmin && orgUser.role == null) {
    return CourseRole.COURSE_ADMIN;
  }
  return orgUser.role;
};

function getMaxRole(
  orgUser: OrgUserType,
  user: UserType,
  sectionUsersForUser: { role: SectionRole }[],
  courseUsersForUser: { role: CourseRole }[],
  courseGroupingUsers: { role: CourseRole }[],
): MaxRole {
  if (user.role === UserRole.SUPER_ADMIN) return MaxRole.SUPER_ADMIN;
  if (user.role === UserRole.GLOBAL_ADMIN) return MaxRole.GLOBAL_ADMIN;

  if (orgUser.role === OrgRole.ADMIN) return MaxRole.ORG_ADMIN;

  const hasCourseAdminForGrouping = courseGroupingUsers.some(
    (cu) => cu.role === CourseRole.COURSE_ADMIN,
  );
  if (hasCourseAdminForGrouping) return MaxRole.COURSE_ADMIN;

  const hasCourseAdmin = courseUsersForUser.some((cu) => cu.role === CourseRole.COURSE_ADMIN);
  if (hasCourseAdmin) return MaxRole.COURSE_ADMIN;

  const hasInstructor = sectionUsersForUser.some((cu) => cu.role === SectionRole.INSTRUCTOR);
  if (hasInstructor) return MaxRole.INSTRUCTOR;

  const hasSectionAssistant = sectionUsersForUser.some(
    (cu) => cu.role === SectionRole.SECTION_ASSISTANT,
  );
  if (hasSectionAssistant) return MaxRole.SECTION_ASSISTANT;

  return MaxRole.STUDENT;
}

type SortField = "name" | "email" | "maxRole" | "external_id" | "invite_status";

interface AdminUsersListProps {
  onShowBulkImportModal: (importType: BulkImportType) => void;
}

export const AdminUsersList: FC<AdminUsersListProps> = ({ onShowBulkImportModal }) => {
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const dispatch = useAppDispatch();
  const allUsers = useListSelector(userSelectors.selectAll);
  const orgUsers = useAppSelector(orgUserSelectors.selectAll);
  const userInviteStatuses = useAppSelector(selectUserInviteStatuses);
  const courseUsers = useAppSelector(courseUserSelectors.selectAll);
  const sectionUsers = useListSelector(selectEnabledSectionUsersByRole);
  const courseGroupingUsers = useAppSelector(courseGroupingUserSelectors.selectAll);
  const sections = useListSelector(sectionSelectors.selectAll);
  const currentOrg = useAppSelector(selectCurrentOrg);
  const { canAuthorOrg, canImpersonateUser, canSeeAllOrgUsers } = useCapabilities();
  const editModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const canToggleDisabled = canAuthorOrg;
  const canChangeOrgRole = canAuthorOrg;
  const isSmallScreen = !useIsMdOrLarger();

  const [, copyToClipboard] = useCopyToClipboard();

  useEffect(() => {
    storeApi.section_users.list({});
    storeApi.course_users.list({});
    storeApi.course_grouping_users.list({});
  }, []);

  useFetchUserInviteStatuses();

  // OrgUsers and SectionUsers are both links to the User model, and to Org and Section separately,
  // and both have a "role" string attached.
  // In this view we'd like to show a list of all users that have an OrgUser, their role, and the
  // amount of SectionUsers that they have.

  const entries: AdminUserEntry[] = orgUsers
    .flatMap((orgUser) => {
      const user = allUsers.find((u) => u.id === orgUser.user_id);
      if (!user || orgUser.role === OrgRole.RECORDING_AGENT) {
        return [];
      }

      if (!canSeeAllOrgUsers && userHasGlobalAuthority(user)) {
        return [];
      }

      const sectionUsersForUser = sectionUsers.filter((su) => su.user_id === orgUser.user_id);
      const courseUsersForUser = courseUsers.filter((cu) => cu.user_id === orgUser.user_id);
      const courseGroupingUsersForUser = courseGroupingUsers.filter(
        (cgu) => cgu.user_id === orgUser.user_id,
      );

      const tooltipSectionRoleTable = sectionUsersForUser.map((cu) => {
        const section = sections.find((s) => s.id === cu.section_id);
        const role = getSectionResourceRole(cu.role);

        return (
          <div key={cu.id}>
            <strong>{section?.title}</strong>: {t(`roles.section_${role}`)}
          </div>
        );
      });

      const maxRole = getMaxRole(
        orgUser,
        user,
        sectionUsersForUser,
        courseUsersForUser,
        courseGroupingUsersForUser,
      );

      return [
        {
          user,
          orgUser,
          tooltipSectionRoleTable,
          inviteStatus: userInviteStatuses[user.id] || InviteStatus.NOT_SENT,
          maxRole,
          isCourseAdmin: courseUsersForUser.length > 0 || courseGroupingUsersForUser.length > 0,
        },
      ];
    })
    .toSorted((a, b) => {
      const resolveSortRole = (entry: AdminUserEntry): OrgRole | CourseRole | null => {
        if (entry.orgUser.role == null) {
          return entry.isCourseAdmin ? CourseRole.COURSE_ADMIN : null;
        }
        return entry.orgUser.role;
      };
      const roleDiff =
        getOrgRoleSortOrder(resolveSortRole(a)) - getOrgRoleSortOrder(resolveSortRole(b));
      return roleDiff !== 0 ? roleDiff : sortByFirstName(a.user, b.user);
    });

  const canSeeExternalUserIds = canAuthorOrg && entries.some((e) => e.orgUser.external_user_id);

  const getOrgUserSortValue = useCallback(
    (entry: AdminUserEntry, field: SortField): number | string | null => {
      switch (field) {
        case "name":
          return `${(entry.user.first_name ?? "").toLowerCase()} ${(entry.user.last_name ?? "").toLowerCase()}`.trim();
        case "email":
          return entry.user.email ?? "";
        case "maxRole":
          return MAX_ROLE_SORT_ORDER[entry.maxRole];
        case "external_id":
          return entry.orgUser.external_user_id ?? "";
        case "invite_status":
          return entry.inviteStatus;
        default:
          return null;
      }
    },
    [],
  );

  const onChangeRole = async (newRole: OrgRole | CourseRole | null, entry: AdminUserEntry) => {
    if (newRole === CourseRole.COURSE_ADMIN) {
      manageCourseAdmin(entry);
      return;
    }

    if (entry.isCourseAdmin) {
      await serverManageCourseAdmin(entry.user.id, []);
      const courseUsersForUser = courseUsers.filter((cu) => cu.user_id === entry.user.id);
      courseUsersForUser.forEach((cu) =>
        dispatch(slices.course_users.actions.removeOne({ id: cu.id })),
      );

      const courseGroupingUsersForUser = courseGroupingUsers.filter(
        (cgu) => cgu.user_id === entry.user.id,
      );
      courseGroupingUsersForUser.forEach((cgu) =>
        dispatch(slices.course_grouping_users.actions.removeOne({ id: cgu.id })),
      );
    }

    storeApi.org_users
      .partial_update({
        id: entry.orgUser.id,
        role: newRole,
      })
      .catch(toastLocalizedOperationCatcher("update_user_role"));
  };

  const onImpersonate = async (user: UserType) => {
    const url = await createImpersonationUrl(user.id);

    if (url) {
      copyToClipboard(url);
    } else {
      toastErrorMessage(t("error.toasts.failed_to_impersonate_user"));
    }
  };

  const manageCourseAdmin = (entry: AdminUserEntry) => {
    showConfirmationModal({
      title: t("user_options_menu.manage_course_admin.title"),
      hideCancelConfirmButtons: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      children: (onClose) => (
        <ManageCourseAdminModal
          userId={entry.user.id}
          userName={getUserFullName(entry.user)}
          onClose={onClose}
        />
      ),
    });
  };

  const onToggleDisabled = (orgUser: OrgUserType) => {
    storeApi.org_users.partial_update({
      id: orgUser.id,
      is_disabled: !orgUser.is_disabled,
    });
  };

  const onDownloadCSV = () => {
    if (!currentOrg) {
      toastErrorMessage(t("section_enrollment_report.export_failed"));
      return;
    }
    if (entries.length === 0) {
      toastErrorMessage(t("section_enrollment_report.no_data"));
      return;
    }
    try {
      const { csvData, filename } = generateOrgUsersCSV(
        entries.map((entry) => ({
          first_name: entry.user.first_name,
          last_name: entry.user.last_name,
          email: entry.user.email,
          maxRole: entry.maxRole,
          external_user_id: entry.orgUser.external_user_id,
          invite_status: entry.inviteStatus,
        })),
        currentOrg.name,
        canSeeExternalUserIds,
      );
      downloadCSVFile(csvData, filename);
    } catch {
      toastErrorMessage(t("section_enrollment_report.export_failed"));
    }
  };

  return (
    <div className="h-full overflow-y-auto pb-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="mb-0 hidden md:block">{t("user_options_menu.org_sidebar_title")}</h3>
        <div className={clsx("flex gap-2", isSmallScreen && "w-full")}>
          <SquareButton
            aria-label={t("admin.add_user.add_user")}
            className="flex-1 md:flex-none"
            disabled={!editModeEnabled}
            eventName="Org Users - Add new"
            kind="tertiary"
            size="xs"
            onClick={() => setShowAddUserModal(true)}
          >
            {t("admin.add_user.add_user")}
          </SquareButton>

          <BulkImportMenu disabled={!editModeEnabled} onSelectImportType={onShowBulkImportModal} />

          <SquareButton
            aria-label={t("user_options_menu.download_csv")}
            className="flex-1 md:flex-none"
            disabled={entries.length === 0}
            eventName="Org Users - Download CSV"
            kind="tertiary"
            size="xs"
            onClick={onDownloadCSV}
          >
            <IconText
              iconStart={<ArrowDownToLineIcon />}
              text={t("user_options_menu.download_csv")}
            />
          </SquareButton>
        </div>
      </div>

      <ScrollableTableContainer className="mb-0">
        <SortableTableWrapper<AdminUserEntry, SortField>
          data={entries}
          getSortValue={getOrgUserSortValue}
        >
          <SortableTable>
            <thead>
              <tr>
                <SortableHeader field="name" label={t("user_options_menu.table_header_name")} />
                <SortableHeader field="email" label={t("user_options_menu.table_header_email")} />
                <SortableHeader
                  field="maxRole"
                  label={t("user_options_menu.table_header_max_role")}
                />
                {canSeeExternalUserIds && (
                  <SortableHeader
                    field="external_id"
                    label={t("user_options_menu.table_header_external_id")}
                  />
                )}
                <SortableHeader
                  field="invite_status"
                  label={t("user_options_menu.table_header_invite_status")}
                />
              </tr>
            </thead>

            <SortableBody<AdminUserEntry>>
              {(sortedData) =>
                sortedData.map((entry) => (
                  <AdminUserRow
                    entry={entry}
                    key={entry.user.id}
                    showExternalId={canSeeExternalUserIds}
                    onChangeRole={canChangeOrgRole ? onChangeRole : undefined}
                    onImpersonate={canImpersonateUser ? onImpersonate : undefined}
                    onToggleDisabled={canToggleDisabled ? onToggleDisabled : undefined}
                  />
                ))
              }
            </SortableBody>
          </SortableTable>
        </SortableTableWrapper>
      </ScrollableTableContainer>
      {showAddUserModal && <AddUserModal onClose={() => setShowAddUserModal(false)} />}
    </div>
  );
};

type AdminUserRowProps = {
  entry: AdminUserEntry;
  onChangeRole?: (newRole: OrgRole | CourseRole | null, entry: AdminUserEntry) => void;
  onImpersonate?: (user: UserType) => void;
  onToggleDisabled?: (orgUser: OrgUserType) => void;
  showExternalId?: boolean;
};

export const AdminUserRow: FC<AdminUserRowProps> = ({
  entry,
  onChangeRole,
  onImpersonate,
  onToggleDisabled,
  showExternalId = false,
}) => {
  const { value: isModalOpen, setTrue: openModal, setFalse: closeModal } = useBoolean(false);
  const hasActions = Boolean(onChangeRole || onImpersonate || onToggleDisabled);
  const { user, orgUser } = entry;
  const displayRole = getDisplayOrgRole(user, orgUser, entry.isCourseAdmin);

  return (
    <TR className="items-center justify-between">
      <TD className={clsx(orgUser.is_disabled && "italic text-black-tint-70")}>
        <div className="flex items-center gap-1">
          <OrgRoleIconWithTooltip extraContent={entry.tooltipSectionRoleTable} role={displayRole} />

          <span className="max-w-m truncate" title={getUserFullName(user)}>
            {getUserFullName(user)}
          </span>
        </div>
      </TD>

      <TD className="body-xs relative">{user.email}</TD>

      <TD className="body-xs relative">{t(`user_options_menu.max_role.${entry.maxRole}`)}</TD>

      {showExternalId && <TD className="body-xs relative">{orgUser.external_user_id || "-"}</TD>}

      <TD className="body-xs flex items-center justify-between">
        <span>{t(`invite_status.${entry.inviteStatus}`)}</span>
        {hasActions && (
          <div className="">
            <AdminUserOptionsMenu
              currentRole={displayRole}
              inviteStatus={entry.inviteStatus}
              isDisabled={orgUser.is_disabled}
              user={user}
              onChangeRole={onChangeRole ? (newRole) => onChangeRole(newRole, entry) : undefined}
              onEdit={openModal}
              onImpersonate={onImpersonate ? () => onImpersonate(user) : undefined}
              onToggleDisabled={onToggleDisabled ? () => onToggleDisabled(orgUser) : undefined}
            />
          </div>
        )}

        <EditOrgUserModal open={isModalOpen} orgUser={orgUser} user={user} onClose={closeModal} />
      </TD>
    </TR>
  );
};
